import { create, type StoreApi, type UseBoundStore } from 'zustand';

import type { WrappedSession } from '../sip/ua';

export interface SessionInfo {
  jwt: string;
  sipExtension: string;
  sipDomain: string;
  sipWssUrl: string;
}

export interface WidgetError {
  code: string;
  message: string;
}

export type WidgetStatus =
  | 'booting'
  | 'authenticating'
  | 'registering'
  | 'ready'
  | 'error';

export interface CallSession {
  callId: string;
  direction: 'inbound' | 'outbound';
  remote: string;
  state: 'ringing' | 'answered' | 'on-hold' | 'ended';
  muted: boolean;
  startedAt: number;
  answeredAt?: number;
  endedAt?: number;
}

export type Presence = 'online' | 'offline' | 'busy';

export type WrapUpMode = 'create' | 'edit';

export interface WrapUpState {
  open: boolean;
  callId: string | null;
  mode: WrapUpMode;
  disposition: string | null;
  notes: string;
  saving: boolean;
  saveError: string | null;
  pending: boolean;
}

const initialWrapUp: WrapUpState = {
  open: false,
  callId: null,
  mode: 'create',
  disposition: null,
  notes: '',
  saving: false,
  saveError: null,
  pending: false,
};

export interface WidgetState {
  status: WidgetStatus;
  session: SessionInfo | null;
  error: WidgetError | null;
  call: CallSession | null;
  presence: Presence;
  wrapUp: WrapUpState;
  setAuthenticating: () => void;
  setRegistering: (session: SessionInfo) => void;
  setReady: () => void;
  setError: (error: WidgetError) => void;
  reset: () => void;
  // call lifecycle
  setIncomingCall: (input: { callId: string; remote: string }) => void;
  setOutgoingCall: (input: { callId: string; remote: string }) => void;
  markAnswered: () => void;
  markHold: () => void;
  markUnhold: () => void;
  markMuted: (muted: boolean) => void;
  markEnded: () => void;
  // presence
  setPresence: (p: Presence) => void;
  // wrap-up actions
  openWrapUp: (
    callId: string,
    mode: WrapUpMode,
    prefill?: { disposition?: string | null; notes?: string; pending?: boolean },
  ) => void;
  setDisposition: (value: string | null) => void;
  setNotes: (value: string) => void;
  saveWrapUpStart: () => void;
  saveWrapUpSuccess: (opts: { pending: boolean }) => void;
  saveWrapUpError: (message: string) => void;
  closeWrapUp: () => void;
  // F4.2-B3: multi-leg transfer state
  consultSession: WrappedSession | null;
  transferState: 'idle' | 'consulting' | 'transferring' | 'conferencing';
  transferTarget: string | null;
  setConsult: (session: WrappedSession, target: string) => void;
  setTransferring: () => void;
  /**
   * F4.5.1: orchestrator transitioned consulting → conferencing. Setting
   * `transferState='conferencing'` flips the TransferPanel to
   * `conference-active` mode (showing "Quitar A/B" + "Cerrar conferencia"
   * controls). Without this, the panel stayed in `consult-active` mode
   * after addToConference() succeeded.
   */
  setConferencing: () => void;
  clearConsult: () => void;
}

const initialState = {
  status: 'booting' as const,
  session: null,
  error: null,
  call: null,
  presence: 'offline' as Presence,
  wrapUp: initialWrapUp,
  consultSession: null,
  transferState: 'idle' as const,
  transferTarget: null,
};

export function createStore(): UseBoundStore<StoreApi<WidgetState>> {
  return create<WidgetState>((set) => ({
    ...initialState,
    setAuthenticating: () => set({ status: 'authenticating', error: null }),
    setRegistering: (session) => set({ status: 'registering', session, error: null }),
    setReady: () => set({ status: 'ready', error: null }),
    setError: (error) => set({ status: 'error', error }),
    reset: () => set(initialState),
    // call lifecycle
    setIncomingCall: ({ callId, remote }) =>
      set({
        call: { callId, direction: 'inbound', remote, state: 'ringing', muted: false, startedAt: Date.now() },
        presence: 'busy',
      }),
    setOutgoingCall: ({ callId, remote }) =>
      set({
        call: { callId, direction: 'outbound', remote, state: 'ringing', muted: false, startedAt: Date.now() },
        presence: 'busy',
      }),
    markAnswered: () =>
      set((s) => ({ call: s.call ? { ...s.call, state: 'answered', answeredAt: Date.now() } : null })),
    markHold: () =>
      set((s) => ({ call: s.call ? { ...s.call, state: 'on-hold' } : null })),
    markUnhold: () =>
      set((s) => ({ call: s.call ? { ...s.call, state: 'answered' } : null })),
    markMuted: (muted) =>
      set((s) => ({ call: s.call ? { ...s.call, muted } : null })),
    markEnded: () =>
      set((s) => ({ call: s.call ? { ...s.call, state: 'ended', endedAt: Date.now() } : null, presence: 'online' })),
    // presence
    setPresence: (p) => set({ presence: p }),
    // F4.2-B3: multi-leg transfer actions
    setConsult: (session, target) =>
      set({ consultSession: session, transferTarget: target, transferState: 'consulting' }),
    setTransferring: () =>
      set({ transferState: 'transferring' }),
    setConferencing: () =>
      set({ transferState: 'conferencing' }),
    clearConsult: () =>
      set({ consultSession: null, transferTarget: null, transferState: 'idle' }),
    // wrap-up actions
    openWrapUp: (callId, mode, prefill) =>
      set({
        wrapUp: {
          open: true,
          callId,
          mode,
          disposition: prefill?.disposition ?? null,
          notes: prefill?.notes ?? '',
          saving: false,
          saveError: null,
          pending: prefill?.pending ?? false,
        },
      }),
    setDisposition: (value) =>
      set((s) => ({ wrapUp: { ...s.wrapUp, disposition: value } })),
    setNotes: (value) =>
      set((s) => ({ wrapUp: { ...s.wrapUp, notes: value } })),
    saveWrapUpStart: () =>
      set((s) => ({ wrapUp: { ...s.wrapUp, saving: true, saveError: null } })),
    saveWrapUpSuccess: ({ pending }) =>
      set((s) => ({ wrapUp: { ...s.wrapUp, saving: false, pending, saveError: null } })),
    saveWrapUpError: (message) =>
      set((s) => ({ wrapUp: { ...s.wrapUp, saving: false, saveError: message } })),
    closeWrapUp: () => set({ wrapUp: initialWrapUp }),
  }));
}

export const useWidgetStore = createStore();
